import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { AppHeader, SourceNote } from "@/components/Layout";
import { api } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { formatDate, imageUrl } from "@/lib/blog";

export default function BlogAuthor() {
  const { slug } = useParams();
  const [author, setAuthor] = useState(null);
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api.get("/blog/authors").then((rows) => {
      setAuthor(rows.find((a) => a.slug === slug) || null);
    });
    api.get(`/blog/posts?author=${encodeURIComponent(slug)}`).then(setPosts);
  }, [slug]);

  useSeo({
    title: author ? `${author.name} – posts` : "Author",
    description: author ? (author.bio || `Posts by ${author.name} on Food Recall Alerts.`) : "Author",
    path: `/blog/author/${slug}`,
  });

  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader />
      <div className="mx-auto max-w-3xl px-5 py-10 sm:py-14" data-testid={`author-${slug}`}>
        <nav className="mb-6 text-xs text-slate-500">
          <Link to="/blog" className="hover:text-slate-900">Blog</Link>
        </nav>
        {author ? (
          <header className="flex items-start gap-5">
            {author.avatar_url && (
              <img src={imageUrl(author.avatar_url)} alt="" className="h-20 w-20 rounded-full object-cover" />
            )}
            <div>
              <h1 className="font-display text-3xl font-extrabold text-slate-900">{author.name}</h1>
              {author.bio && <p className="mt-2 text-slate-600">{author.bio}</p>}
              <div className="mt-3 flex gap-3 text-sm">
                {author.x_url && <a href={author.x_url} target="_blank" rel="noreferrer noopener" className="text-slate-500 hover:text-slate-900">X / Twitter</a>}
                {author.linkedin_url && <a href={author.linkedin_url} target="_blank" rel="noreferrer noopener" className="text-slate-500 hover:text-slate-900">LinkedIn</a>}
              </div>
            </div>
          </header>
        ) : (
          <p className="text-slate-500">Author not found.</p>
        )}

        <ul className="mt-10 space-y-4">
          {posts.map((p) => (
            <li key={p.id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`author-post-${p.slug}`}>
              <Link to={`/blog/${p.slug}`} className="font-display text-xl font-bold text-slate-900 hover:text-red-700">
                {p.title}
              </Link>
              <p className="mt-1 text-xs text-slate-500">{formatDate(p.publish_date)}</p>
              <p className="mt-2 line-clamp-2 text-slate-600">{p.excerpt}</p>
            </li>
          ))}
          {posts.length === 0 && <p className="text-slate-500">No posts yet.</p>}
        </ul>
        <SourceNote className="mt-12" />
      </div>
    </div>
  );
}
